/**
 * Apples to Apples' asks, projected from state and diffed by `syncRequests`. No ask targets
 * a host: the judge is a player. Ids are round-scoped (`ready` too) so a new round never
 * inherits last round's answers.
 */
import { cardText } from './content.js';
import { APPLES_MIN_PLAYERS } from './state.js';
/**
 * Game-prefixed because `@gsf/games` is a flat barrel. Callers name asks through this,
 * never by building the string.
 */
export const APPLES_REQUESTS = {
    ready: (round) => `ready:${String(round)}`,
    prompt: (round) => `prompt:${String(round)}`,
    play: (round) => `play:${String(round)}`,
    wait: (round) => `wait:${String(round)}`,
    pick: (round) => `pick:${String(round)}`,
    next: (round) => `next:${String(round)}`,
};
/** A card as an option: the id is the card's, the label is what it says. */
function optionFor(ctx, cardId) {
    return { id: cardId, label: cardText(ctx.content, cardId) };
}
/**
 * Completes when every player has pressed. Under the minimum it becomes a `none`; the id
 * is unchanged, so presses already collected survive.
 */
function readyAsk(state, ctx) {
    const short = ctx.players.length < APPLES_MIN_PLAYERS;
    return {
        id: APPLES_REQUESTS.ready(state.round),
        targets: 'allPlayers',
        prompt: short ? 'Waiting for more players' : 'Everyone in?',
        spec: short
            ? {
                kind: 'none',
                message: `Apples to Apples needs ${String(APPLES_MIN_PLAYERS)} players — ${String(ctx.players.length)} so far.`,
            }
            : { kind: 'press', label: "I'M READY" },
        revealSubmittedToSelf: true,
        onComplete: { type: 'beginRound' },
    };
}
/** Candidates are secret from the room: a discarded card reveals what the judge nearly asked. */
function promptAsk(state, ctx) {
    if (state.judgeId === null || state.candidates.length === 0)
        return null;
    return {
        id: APPLES_REQUESTS.prompt(state.round),
        targets: { participantIds: [state.judgeId] },
        prompt: 'You are the judge. Pick a green apple and read it out.',
        spec: {
            kind: 'choice',
            options: state.candidates.map((id) => optionFor(ctx, id)),
        },
        onComplete: { type: 'promptChosen' },
    };
}
/**
 * No `deadlineAt`: the reducer's `cardsIn` timer moves the game, and two clocks would drift.
 * A card stays in hand until the round ends, so options, echo and denominator stay stable.
 */
function playAsk(state, ctx) {
    const specs = {};
    const targets = [];
    for (const player of ctx.players) {
        if (player.id === state.judgeId)
            continue;
        const hand = state.hands[player.id] ?? [];
        // A hand the deck could not fill. Not targeted, because a targeted phone that cannot
        // answer is a request that never completes — they get the waiting card instead.
        if (hand.length === 0)
            continue;
        targets.push(player.id);
        specs[player.id] = { kind: 'choice', options: hand.map((id) => optionFor(ctx, id)) };
    }
    if (targets.length === 0)
        return null;
    return {
        id: APPLES_REQUESTS.play(state.round),
        targets: { participantIds: targets },
        prompt: state.greenId === null ? 'Play a card' : `Play your best "${cardText(ctx.content, state.greenId)}"`,
        spec: specs,
        revealSubmittedToSelf: true,
        onComplete: { type: 'cardsIn' },
    };
}
/**
 * Anonymous: options are card ids on a shuffled table. No deadline: a randomly picked
 * winner is worse than a slow judge.
 */
function pickAsk(state, ctx) {
    if (state.judgeId === null || state.played.length === 0)
        return null;
    return {
        id: APPLES_REQUESTS.pick(state.round),
        targets: { participantIds: [state.judgeId] },
        prompt: state.greenId === null
            ? 'Pick the winner'
            : `Which one is the best "${cardText(ctx.content, state.greenId)}"?`,
        spec: {
            kind: 'choice',
            options: state.played.map((play) => optionFor(ctx, play.cardId)),
        },
        onComplete: { type: 'winnerChosen' },
    };
}
/**
 * `onSubmission`, so the first press advances: a request needing everybody would stall on
 * one phone in a pocket. Targets every player so an absent judge cannot hold the game.
 */
function nextAsk(state, ctx) {
    if (ctx.players.length === 0)
        return null;
    // The label must not say "next round" when the press ends the game. For `firstToN`,
    // this round's award is already in `ctx.scores`.
    const condition = ctx.config.winCondition ?? 'rounds';
    const won = condition === 'firstToN' &&
        [...ctx.scores.values()].some((score) => score >= (ctx.config.winTarget ?? 5));
    const last = condition === 'rounds' ? state.round >= ctx.config.rounds : won;
    return {
        id: APPLES_REQUESTS.next(state.round),
        targets: 'allPlayers',
        prompt: last ? 'That was the last round.' : 'Ready for the next one?',
        spec: { kind: 'press', label: last ? 'SEE THE SCORES' : 'NEXT ROUND' },
        onSubmission: { type: 'nextRound' },
    };
}
/** A phone with no control reads as broken, so whoever is waiting gets a routed `none`. */
function waitAsk(state, ctx) {
    let waiting = [];
    let message = '';
    if (state.phase === 'playing') {
        // The judge, plus anybody the deck could not deal a card to.
        waiting = ctx.players
            .filter((player) => player.id === state.judgeId ||
            ((state.hands[player.id] ?? []).length === 0 &&
                !state.played.some((play) => play.participantId === player.id)))
            .map((player) => player.id);
        message =
            state.judgeId === null
                ? 'Waiting for the round.'
                : 'You are the judge — waiting for everyone to play.';
    }
    else if (state.phase === 'judging') {
        waiting = ctx.players.filter((player) => player.id !== state.judgeId).map((player) => player.id);
        message = 'Cards are in. The judge is deciding.';
    }
    else {
        return null;
    }
    if (waiting.length === 0)
        return null;
    return {
        id: APPLES_REQUESTS.wait(state.round),
        targets: { participantIds: waiting },
        prompt: message,
        spec: { kind: 'none', message },
    };
}
/** Everything this state is asking for. Must stay pure: `syncRequests` diffs successive calls. */
export function requestsFor(state, ctx) {
    const asks = [];
    switch (state.phase) {
        case 'lobby':
            asks.push(readyAsk(state, ctx));
            break;
        case 'prompt': {
            const prompt = promptAsk(state, ctx);
            if (prompt)
                asks.push(prompt);
            break;
        }
        case 'playing': {
            const play = playAsk(state, ctx);
            if (play)
                asks.push(play);
            const wait = waitAsk(state, ctx);
            if (wait)
                asks.push(wait);
            break;
        }
        case 'judging': {
            const pick = pickAsk(state, ctx);
            if (pick)
                asks.push(pick);
            const wait = waitAsk(state, ctx);
            if (wait)
                asks.push(wait);
            break;
        }
        case 'reveal': {
            // No clock — see `nextAsk`.
            const next = nextAsk(state, ctx);
            if (next)
                asks.push(next);
            break;
        }
        case 'over':
            // The show runner owns what follows; a control here would do nothing.
            break;
    }
    return asks;
}
//# sourceMappingURL=requests.js.map